import AsyncComponent from '@/components/common/AsyncComponent';
import ContextMenu from '@/components/common/ContextMenu';
import CopyButton from '@/components/common/CopyButton';
import CurrencyIcon from '@/components/common/CurrencyIcon';
import Divider from '@/components/common/Divider';
import Loader from '@/components/common/Loader';
import FilterSelect from '@/components/inputs/FilterSelect';
import { SSIProfileBankAccountsResponse } from '@/types/sso';
import { addSpacingToCharacter } from '@/utils/format';
import useContextMenu from '@/utils/hooks/useContextMenu';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import QRCode from 'qrcode';
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
    MdCheck,
    MdChevronLeft,
    MdChevronRight,
    MdContentCopy,
    MdOutlineAccountBalance,
    MdOutlineAccountBalanceWallet,
    MdOutlineQrCodeScanner
} from 'react-icons/md';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from '../Modal';

interface ModifiedWalletSSI {
    assetType: string;
    currencyId: number;
    currencyName: string;
    digitalAssetName: string;
    active: boolean;
    friendlyName: string;
    networkName: string;
    walletAddress: string;
    walletId: number;
}

export interface PortalSSIModalProps extends ModalProps {}

const defaultFiatCurrency = { label: 'Please select a currency', value: undefined };

export const PortalSSIModal = (props: PortalSSIModalProps) => {
    const { opened } = props;

    const lang = useLanguage();
    const { foundEntity, currenciesApi, ssiProfileBankInfoApi, ssiProfileWalletInfoApi } = useSso();

    const [currencyType, setCurrencyType] = useState<'Fiat' | 'Crypto' | ''>('');
    const [fiatCurrency, setFiatCurrency] = useState<any>(defaultFiatCurrency);

    const { data: cashEntryCurrencies, isLoading: cashEntryCurrenciesLoading } = currenciesApi;
    const { data: bankInfoSSIData, isLoading: isLoadingFiatSSI } = ssiProfileBankInfoApi;
    const { data: walletSSIData, isLoading: isLoadingCryptoSSI } = ssiProfileWalletInfoApi;

    const bankInfoSSIProfiles = useMemo(() => {
        if (bankInfoSSIData && bankInfoSSIData.length > 0) {
            return bankInfoSSIData
                .map((ssi) => ssi.profileInfos)
                .flat()
                .filter((ssi) => ssi.active);
        }
        return [];
    }, [bankInfoSSIData]);

    const hasBankInfoSSI = useMemo(() => !(bankInfoSSIProfiles.length > 0), [bankInfoSSIProfiles]);

    const walletSSIProfiles = useMemo(() => {
        if (walletSSIData && walletSSIData.length > 0) {
            const returnValues = walletSSIData
                .flatMap((ssi) => ssi.profileInfos)
                .filter((ssi) => ssi.active)
                .flatMap((profile) => {
                    const { supportedAssets, ...restProfiles } = profile;
                    return profile.supportedAssets.map((asset) => ({
                        ...restProfiles,
                        ...asset
                    }));
                });

            returnValues.sort((a, b) => a.digitalAssetName.localeCompare(b.digitalAssetName));
            return returnValues;
        }
        return [];
    }, [walletSSIData]);

    const hasWalletSSI = useMemo(() => walletSSIProfiles.length > 0, [walletSSIProfiles]);

    const currencyOptions = useMemo(() => {
        const uniqueCurrencies = bankInfoSSIProfiles.map((ssi) => ssi.baseCurrencyId);
        return bankInfoSSIProfiles && cashEntryCurrencies
            ? cashEntryCurrencies
                  .filter((ccy) => ccy.enabledForDeposit && ccy.type === 'FIAT' && uniqueCurrencies.includes(ccy.id))
                  .map((currency) => {
                      return {
                          label: currency.digitalAssetName || currency.name,
                          value: currency.id
                      };
                  })
            : [];
    }, [bankInfoSSIProfiles, cashEntryCurrencies]);

    const fiatSSIProfileInfo = useMemo(() => {
        return bankInfoSSIProfiles.find((ssi) => ssi.baseCurrencyId === fiatCurrency.value);
    }, [fiatCurrency, bankInfoSSIProfiles]);

    useEffect(() => {
        if (currencyOptions && currencyOptions.length > 0 && currencyType === 'Fiat') {
            const usd = currencyOptions.find((ccy) => ccy.label === 'USD');
            if (usd) setFiatCurrency(usd);
            else setFiatCurrency(currencyOptions[0]);
        }
    }, [currencyOptions, currencyType]);

    useEffect(() => {
        if (opened) {
            currenciesApi.mutate();
            ssiProfileBankInfoApi.mutate();
            ssiProfileWalletInfoApi.mutate();
        }
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size={currencyType === 'Crypto' ? 'max-w-[900px]' : 'max-w-[650px]'}>
            <ModalHeader>
                <ModalTitle>
                    <div className="flex items-center gap-2">
                        {currencyType !== '' && (
                            <MdChevronLeft
                                className="w-5 h-5 cursor-pointer"
                                onClick={() => {
                                    setCurrencyType('');
                                    setFiatCurrency(defaultFiatCurrency);
                                }}
                            />
                        )}
                        {lang.portal.depositFunds}
                    </div>
                </ModalTitle>
                <ModalClose {...props} />
            </ModalHeader>
            <Divider neutral />
            <div className="py-4 w-full h-[600px] overflow-y-auto px-2 sm:px-4">
                {currencyType === '' && (
                    <div className=" flex flex-col gap-3 text-brand-text">
                        <AsyncComponent isLoading={isLoadingFiatSSI || isLoadingCryptoSSI}>
                            <div
                                title={hasBankInfoSSI ? '' : 'Your account does not support fiat deposits.'}
                                className={cn(
                                    'relative text-brand-text flex gap-3 border border-brand-neutral-700 p-4 h-30 rounded-md w-full',
                                    {
                                        'group hover:text-brand-text hover:border-brand-neutral-600 cursor-pointer':
                                            hasBankInfoSSI,
                                        'opacity-50 cursor-not-allowed': !hasBankInfoSSI
                                    }
                                )}
                                onClick={() => {
                                    if (hasBankInfoSSI) setCurrencyType('Fiat');
                                }}>
                                <span>
                                    <MdOutlineAccountBalance className="w-5 h-5 mt-1" />
                                </span>
                                <div className="flex flex-col">
                                    <span className="text-brand-neutral-300 font-semibold text-sm md:text-base">
                                        {lang.portal.fiatViaBankTransfer}
                                    </span>
                                    <span className="group-hover:text-brand-neutral-400 text-xs md:text-sm text-brand-neutral-500 pr-5">
                                        {lang.portal.makeADepositFromYourWhitelistedBankAccount}
                                    </span>
                                </div>
                                <MdChevronRight className="w-6 h-6 absolute right-4 self-center" />
                            </div>
                            <div
                                title={hasWalletSSI ? '' : 'Your account does not support crypto deposits.'}
                                className={cn(
                                    'relative text-brand-text flex gap-3 border border-brand-neutral-700 p-4 h-30 rounded-md w-full',
                                    {
                                        'group hover:text-brand-text hover:border-brand-neutral-600 cursor-pointer':
                                            hasWalletSSI,
                                        'opacity-50 cursor-not-allowed': !hasWalletSSI
                                    }
                                )}
                                onClick={() => {
                                    if (hasWalletSSI) setCurrencyType('Crypto');
                                }}>
                                <span>
                                    <MdOutlineAccountBalanceWallet className="w-5 h-5 mt-1" />
                                </span>
                                <div className="flex flex-col gap-1">
                                    <span className="text-brand-neutral-300 font-semibold text-sm md:text-base">
                                        {lang.portal.cryptoViaWalletAddress}
                                    </span>
                                    <span className="group-hover:text-brand-neutral-400 text-xs md:text-sm text-brand-neutral-500 pr-5">
                                        {lang.portal.makeADepositToYourWhitelistedWalletAddress}
                                    </span>
                                </div>
                                <MdChevronRight className="w-6 h-6 absolute right-4 self-center" />
                            </div>
                        </AsyncComponent>
                    </div>
                )}
                {currencyType === 'Fiat' && (
                    <div className="flex flex-col h-full gap-3 text-brand-text">
                        <div className="flex flex-col gap-1 text-sm">
                            <span className="text-sm">{lang.portal.depositFiatDescription}</span>
                            <AsyncComponent isLoading={cashEntryCurrenciesLoading}>
                                <FilterSelect
                                    selected={fiatCurrency}
                                    options={currencyOptions}
                                    enableCurrencyIcon
                                    enableSorting
                                    onChange={(e) => setFiatCurrency(e)}
                                />
                            </AsyncComponent>
                        </div>
                        <span className="text-sm">{lang.portal.transferFundsFromYourWhitelistedBankAccount}</span>
                        {isLoadingFiatSSI ? (
                            <Loader className="w-4 h-4" />
                        ) : (
                            <Fragment>
                                {fiatSSIProfileInfo && <BankAccountInfo ssiData={fiatSSIProfileInfo} />}
                            </Fragment>
                        )}
                    </div>
                )}
                {currencyType === 'Crypto' && (
                    <div className="flex flex-col h-full text-brand-text">
                        <span className="text-sm">{lang.portal.depositCryptoDescription}</span>
                        <div className="flex text-sm font-semibold pt-5 py-2 border-b border-brand-neutral-400">
                            <div className="flex-1 basis-0"> {lang.portal.token}</div>
                            <div className="flex-1 basis-0">{lang.portal.network}</div>
                            <div className="flex-1 basis-0">{lang.portal.chain}</div>
                            <div className="flex-[4_1_0] basis-0 hidden md:block">{lang.portal.address}</div>
                        </div>
                        {isLoadingCryptoSSI ? (
                            <Loader className="w-4 h-4 mt-2" />
                        ) : (
                            <WalletAddressInfo walletSSIProfiles={walletSSIProfiles} />
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

type WalletAddressInfoProps = {
    walletSSIProfiles: ModifiedWalletSSI[];
};

const WalletAddressInfo = ({ walletSSIProfiles }: WalletAddressInfoProps) => {
    const [qrUrl, setQrUrl] = useState('');

    const lang = useLanguage();

    const contextMenu = useContextMenu();
    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        contextMenu;

    return (
        <div className="flex flex-col text-sm text-brand-text divide-y divide-brand-neutral-500 gap-4 md:gap-2 pb-4">
            {walletSSIProfiles.length > 0 ? (
                walletSSIProfiles.map((profile, index) => {
                    const name = profile.digitalAssetName || profile.currencyName;
                    return (
                        <div key={name + index} className="flex pt-3 md:pt-1.5">
                            <div className="flex flex-col gap-2 md:gap-0 w-full">
                                <div className="flex">
                                    <div className="flex-1 basis-0 flex items-center">
                                        <CurrencyIcon
                                            ccy={profile.digitalAssetName || profile.currencyName}
                                            className="w-4 h-4 mr-1.5"
                                        />
                                        {profile.digitalAssetName || profile.currencyName}
                                    </div>
                                    <div className="flex-1 basis-0 flex items-center">{profile.networkName}</div>
                                    <div className="flex-1 basis-0 flex items-center">{profile.assetType}</div>
                                    <div className="flex-[4_1_0] basis-0 items-center gap-3 overflow-auto hidden md:flex">
                                        <CopyButton value={profile.walletAddress}>
                                            {({ copied, copy }) => (
                                                <button
                                                    onClick={copy}
                                                    className="border border-brand-neutral-500 hover:border-brand-neutral-400 rounded-sm p-1">
                                                    {copied ? (
                                                        <MdCheck className="w-4 h-4 text-brand-text" />
                                                    ) : (
                                                        <MdContentCopy className="w-4 h-4 text-brand-text" />
                                                    )}
                                                </button>
                                            )}
                                        </CopyButton>
                                        <span className="flex-1 basis-0 pt-1.5 pb-1 break-all">
                                            {profile.walletAddress}
                                        </span>
                                        <div
                                            className="border border-brand-neutral-500 hover:border-brand-neutral-400 rounded-sm p-1 cursor-pointer"
                                            onClick={(e) => {
                                                QRCode.toDataURL(profile.walletAddress, { width: 300 })
                                                    .then((url) => setQrUrl(url))
                                                    .catch((err) => console.error(err));
                                                handleOpenContextMenu(e);
                                            }}>
                                            <MdOutlineQrCodeScanner className="w-4 h-4 text-brand-text" />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 overflow-auto md:hidden">
                                    <CopyButton value={profile.walletAddress}>
                                        {({ copied, copy }) => (
                                            <span className="flex-1 basis-0 break-all" onClick={copy}>
                                                <div className="flex items-center gap-1.5 text-xs text-brand-neutral-400 border p-2 rounded-sm border-brand-neutral-600">
                                                    <span>
                                                        {copied ? (
                                                            <MdCheck className="w-4 h-4 text-brand-text" />
                                                        ) : (
                                                            <MdContentCopy className="w-4 h-4 text-brand-text" />
                                                        )}
                                                    </span>
                                                    {profile.walletAddress}
                                                </div>
                                            </span>
                                        )}
                                    </CopyButton>
                                </div>
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className="flex pt-2">{lang.portal.noSupportedAssets}</div>
            )}
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                offset={{ x: 16, y: 16 }}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}
                className="!bg-brand-background !w-auto shadow-md !z-[100]">
                {qrUrl && (
                    <div className="p-2">
                        <img src={qrUrl} alt="QR Code" className="w-48 h-48" />
                    </div>
                )}
            </ContextMenu>
        </div>
    );
};

const BankAccountInfo = ({ ssiData }: { ssiData: SSIProfileBankAccountsResponse['profileInfos'][0] }) => {
    const lang = useLanguage();

    const { hasBankInfo, hasCorrespondingBank, hasIntermediaryBank } = useMemo(() => {
        return {
            hasBankInfo:
                ssiData?.bankName ||
                ssiData?.bankAddress ||
                ssiData?.branchName ||
                ssiData?.accountNumber ||
                ssiData?.ibanCode ||
                ssiData?.swiftCode,
            hasCorrespondingBank: ssiData?.correspondentBankName || ssiData?.correspondentBankSwiftCode,
            hasIntermediaryBank: ssiData?.intermediaryBankName || ssiData?.intermediaryBankSwiftCode
        };
    }, [ssiData]);

    const ssiNotes = useMemo(() => {
        return (
            ssiData &&
            ssiData?.note?.split('\n').map((line, index) => (
                <p key={index} className="pb-2 last:pb-0">
                    {line}
                </p>
            ))
        );
    }, [ssiData?.note]);

    return (
        <>
            {ssiData ? (
                <div className="flex flex-col gap-4 pb-4">
                    {hasBankInfo && (
                        <div>
                            <span className="font-bold">{lang.portal.bankDetails}</span>
                            <div className="flex flex-col gap-1 divide-y divide-brand-neutral-500">
                                {ssiData?.beneficiaryName && (
                                    <InfoEntry label="Beneficiary Name" value={ssiData?.beneficiaryName} />
                                )}
                                <InfoEntry label={lang.portal.bankName} value={ssiData?.bankName} />
                                <InfoEntry label={lang.portal.bankAddress} value={ssiData?.bankAddress} />
                                <InfoEntry label={lang.portal.branchName} value={ssiData?.branchName} />
                                <InfoEntry label={lang.portal.accountNumber} value={ssiData?.accountNumber} />
                                <InfoEntry
                                    label={lang.portal.ibanCode}
                                    value={addSpacingToCharacter(ssiData?.ibanCode, 4)}
                                />
                                <InfoEntry label={lang.portal.swiftCode} value={ssiData?.swiftCode} />
                                {ssiData?.note && (
                                    <InfoEntry
                                        label={lang.portal.notes}
                                        value={ssiData?.note}
                                        hideCopyButton
                                        htmlElement={ssiNotes}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {hasCorrespondingBank && (
                        <div>
                            <span className="font-bold">{lang.portal.correspondentBankDetails}</span>
                            <div className="flex flex-col gap-1 divide-y divide-brand-neutral-500">
                                <InfoEntry
                                    label={lang.portal.correspondentBankName}
                                    value={ssiData?.correspondentBankName}
                                />
                                <InfoEntry
                                    label={lang.portal.correspondentSwiftCode}
                                    value={ssiData?.correspondentBankSwiftCode}
                                />
                            </div>
                        </div>
                    )}
                    {hasIntermediaryBank && (
                        <div>
                            <span className="font-bold">{lang.portal.intermediaryBankDetails}</span>
                            <div className="flex flex-col gap-1 divide-y divide-brand-neutral-500">
                                <InfoEntry
                                    label={lang.portal.intermediaryBankName}
                                    value={ssiData?.intermediaryBankName}
                                />
                                <InfoEntry
                                    label={lang.portal.intermediarySwiftCode}
                                    value={ssiData?.intermediaryBankSwiftCode}
                                />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <span className="text-sm text-brand-text">{lang.portal.noBankAccountInformationAvailable}</span>
            )}
        </>
    );
};

const InfoEntry = ({
    label,
    value,
    hideCopyButton = false,
    htmlElement
}: {
    label: string;
    value?: string;
    hideCopyButton?: boolean;
    htmlElement?: JSX.Element | JSX.Element[];
}) => {
    return (
        <>
            {value && (
                <div className="flex gap-2 pt-1">
                    <span className="text-sm flex-1 basis-0 flex items-center text-brand-neutral-400">{label}</span>
                    <div className="text-sm flex-1 basis-0 flex gap-2 items-center overflow-auto">
                        {!hideCopyButton ? (
                            <CopyButton value={value}>
                                {({ copied, copy }) => (
                                    <button
                                        onClick={copy}
                                        className="border border-brand-neutral-500 hover:border-brand-neutral-400 rounded-sm p-1">
                                        {copied ? (
                                            <MdCheck className="w-4 h-4 text-brand-text" />
                                        ) : (
                                            <MdContentCopy className="w-4 h-4 text-brand-text" />
                                        )}
                                    </button>
                                )}
                            </CopyButton>
                        ) : null}

                        <span className="flex-1 basis-0 pt-1.5 pb-1 break-all">{htmlElement || value}</span>
                    </div>
                </div>
            )}
        </>
    );
};
